import { useEffect, useMemo, useState } from 'react';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { HdeOptionList } from '../HDEOptionList';

type ProfessionalMemberAnswer = 'yes' | 'no';

interface ProfessionalMemberStepData {
    isProfessionalMember: boolean | null;
    isLoggedIn?: boolean;
    profileCount?: number;
    onProfessionalMemberSelected?: (value: boolean) => void;
}

/**
 * Asks whether the user wants to use an existing business profile.
 *
 * Behaviour adapts to auth/profile state so logged-in users without a business
 * profile still follow the same manual-entry path as "No, continue with registration".
 */
export function ProfessionalMemberStep({
    setIsValid,
    onDataChange,
    onNext,
    initialData,
}: StepContentProps) {
    const data = initialData as ProfessionalMemberStepData | undefined;
    const onProfessionalMemberSelected = data?.onProfessionalMemberSelected;
    const isLoggedIn = data?.isLoggedIn ?? false;
    const profileCount = data?.profileCount ?? 0;

    const [value, setValue] = useState<ProfessionalMemberAnswer | null>(
        data?.isProfessionalMember === true
            ? 'yes'
            : data?.isProfessionalMember === false
              ? 'no'
              : null,
    );

    const options = useMemo(
        () => [
            {
                value: 'yes' as const,
                label:
                    isLoggedIn && profileCount > 0
                        ? 'Yes, load my business details'
                        : isLoggedIn
                          ? 'Load my business details'
                          : 'Yes, log in to load my business details',
            },
            {
                value: 'no' as const,
                label: 'No, continue with registration',
            },
        ],
        [isLoggedIn, profileCount],
    );

    const isValid = value !== null;

    useEffect(() => {
        setIsValid(isValid);
        const isMember = value === 'yes';
        onDataChange?.({
            isProfessionalMember: value === null ? null : isMember,
        });
    }, [value, isValid, setIsValid, onDataChange]);

    const handleChange = (v: string) => {
        const answer = v as ProfessionalMemberAnswer;
        setValue(answer);
        onDataChange?.({ isProfessionalMember: answer === 'yes' });
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <HdeOptionList
                label="ARE YOU ALREADY AN ARCHIPRO PROFESSIONAL MEMBER?"
                options={options}
                mode="single"
                value={value}
                onChange={handleChange}
                appearance="arrow"
                optionVariant="with-description"
                onSelectAndAdvance={(selectedValue) => {
                    const isProfessionalMember = selectedValue === 'yes';
                    if (onProfessionalMemberSelected) {
                        onProfessionalMemberSelected(isProfessionalMember);
                        return;
                    }
                    onNext();
                }}
            />
        </div>
    );
}
